import { useEffect, useState, useCallback } from "react";
import { api, absoluteImageUrl, formatApiErrorDetail } from "@/lib/api";
import { REAL_JOB_PHOTOS } from "@/config/jobs";

/**
 * Fetch job photos from the backend and merge with the built-in defaults.
 * The Home page's hero slideshow + Recent Jobs section both use this.
 * Ordering: newest DB uploads first, then defaults.
 * Shape: [{ src, alt, title, description, tags }]
 */
export function useJobs() {
  const [items, setItems] = useState(REAL_JOB_PHOTOS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/jobs");
      const dyn = (data || []).map((j) => ({
        src: absoluteImageUrl(j.image_url),
        alt: j.title,
        title: j.title,
        description: j.description || "",
        tags: j.tags || [],
        id: j.id,
        dynamic: true,
      }));
      // Merge: dynamic uploads first (newest), then built-in defaults
      setItems([...dyn, ...REAL_JOB_PHOTOS]);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      setItems(REAL_JOB_PHOTOS);
    } finally {
      setLoading(false);
    }
    // Only module-level imports (`api`, `absoluteImageUrl`,
    // `formatApiErrorDetail`, `REAL_JOB_PHOTOS`) are used — all stable
    // references, none of them are reactive. Empty deps are intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { items, loading, error, reload: load };
}
